import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from 'services'

import { SIGNUPS_QUERY_KEY } from './useSignups'

/**
 * VRG-658 — the staff view of "Refer a brand". Endpoints are
 * `apps/api/src/infrastructure/controllers/referrals.internal.controller.ts`; reads are open
 * to platform admins and managers, the region switch and the row actions to platform admins.
 *
 * Types mirror `toStaffView` / `ReferralGroupRow` / `ReferralProgrammeSummary` on the API
 * key-for-key, UNREDACTED (statuses, ineligibility reasons, Insider refs).
 */

export type StaffReferralStatus =
  | 'sent'
  | 'signed_up'
  | 'granted'
  | 'converted'
  | 'paying'
  | 'capped'
  | 'ended'
  | 'ineligible'
  | 'superseded'

export type StaffReferralChannel = 'link' | 'email_invite' | 'insider'

export interface StaffReferral {
  id: string
  channel: StaffReferralChannel
  status: StaffReferralStatus
  ineligible_reason: string | null
  superseded_by_referral_id: string | null
  referrer: {
    company_id: string | null
    company_name: string | null
    user_id: string | null
    user_name: string | null
    user_email: string | null
  }
  insider_ref: string | null
  invitee: { email: string | null; name: string | null; company_name: string | null }
  signup_id: string | null
  referred_company: { id: string; name: string | null } | null
  created_at: string | null
  attached_at: string | null
  invite_sent_at: string | null
  invite_region: string | null
  invite_send_error: string | null
  first_paid_at: string | null
  reward_window_ends_at: string | null
  credits: {
    net: number
    reversed: number
    cap: number
    percent_of_cap: number
    carry_cents: number
    floor_applied: boolean
  }
  referee_discount: {
    kind: 'monthly' | 'annual' | null
    coupon_id: string
    subscription_id: string | null
    applied_at: string | null
    superseded_reason: string | null
  } | null
  insider_track_status: string | null
}

export interface StaffReferralFilters {
  status?: StaffReferralStatus
  channel?: StaffReferralChannel
  referrerCompanyId?: string
  referrerUserId?: string
  referredCompanyId?: string
  limit?: number
  offset?: number
}

export interface StaffReferralGroup {
  key: string | null
  name: string | null
  email: string | null
  company_id: string | null
  company_name: string | null
  total: number
  by_status: Partial<Record<StaffReferralStatus, number>>
  credits_net: number
  credits_reversed: number
}

export interface ReferralProgrammeSummary {
  from: string | null
  to: string | null
  invites_sent: number
  link_signups: number
  signed_up: number
  granted: number
  became_client: number
  paying: number
  capped: number
  ineligible: number
  superseded: number
  insider_attributed: number
  credits_granted: number
  credits_reversed: number
  credits_forgiven: number
  credits_net: number
}

export interface ReferralInviteRegion {
  region: 'eu_uk' | 'rest_of_world'
  sending_enabled: boolean
  lawful_basis: string
  updated_at: string
  updated_by_user_id: string | null
}

export interface ReferralStaffAction {
  id: string
  referral_id: string
  action: 'reversal' | 'reattribution'
  reason: string
  credits: number
  ledger_entry_id: string | null
  details: Record<string, unknown> | null
  actor: { user_id: string | null; email: string | null; name: string | null }
  created_at: string
}

/** "Referred by" for one company, and the referrals it has sent. */
export interface CompanyReferrals {
  referred_by: StaffReferral | null
  referrals_sent: { data: StaffReferral[]; total: number }
}

/**
 * VRG-665 — the Affiliates tab of `/internal/analytics` (`GET /internal/referrals/dashboard`).
 * Mirrors `ReferralAffiliatesDashboard` + the use case's `credits` key-for-key. Clocks: the
 * funnel, top companies and Insiders are the COHORT created in range; `weekly` is by event
 * date; `exposure` is now.
 */
export interface AffiliatesFunnel {
  sent: number
  signed_up: number
  granted: number
  company_created: number
  became_client: number
  paying: number
  capped_or_ended: number
}

export interface AffiliatesDashboard {
  computed_at: string
  from: string
  to: string
  brand: {
    funnel: AffiliatesFunnel
    sent_breakdown: { invites_delivered: number; invites_undelivered: number; link_signups: number }
    not_counted: { ineligible: number; superseded: number }
  }
  weekly: Array<{ week: string; sent: number; became_client: number; first_paid: number }>
  exposure: { referral_net: number; outstanding: number; companies: number }
  top_companies: Array<{
    company_id: string | null
    name: string | null
    referrals: number
    became_client: number
    paying: number
    credits_net: number
    outstanding: number
  }>
  insiders: {
    funnel: {
      prospects: number
      signed_up: number
      granted: number
      company_created: number
      became_client: number
      paying: number
    }
    insiders_with_prospects: number
    brand_touches_lost_to_insiders: number
    track_status: Record<string, number>
  }
  credits: { granted: number; reversed: number; forgiven: number; net: number }
  cap_per_referral: number
}

/** VRG-665 — `GET /internal/referrals/insiders/firstpromoter`. `figures` is null unless ok. */
export interface InsidersFirstPromoterReport {
  status: 'ok' | 'not_configured' | 'error'
  message?: string
  computed_at: string
  cached: boolean
  range: { from: string; to: string }
  figures: {
    active_insiders: number
    active_insiders_capped: boolean
    pending_insiders: number | null
    leads: number
    signups: number
    customers: number
    commission_earned_cents: number
    commission_paid_cents: number
    commission_owed_cents: number
  } | null
}

const BASE = '/internal/referrals'
export const INTERNAL_REFERRALS_QUERY_KEY = ['internal', 'referrals'] as const

const qs = (params: Record<string, string | number | undefined>) => {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') p.set(k, String(v))
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const useInternalReferrals = (filters: StaffReferralFilters = {}, options?: { enabled?: boolean }) =>
  useQuery<{ data: StaffReferral[]; total: number }>({
    queryKey: [...INTERNAL_REFERRALS_QUERY_KEY, 'list', filters],
    queryFn: async () =>
      (await api.get(`${BASE}${qs({ ...filters, limit: filters.limit ?? 200, offset: filters.offset ?? 0 })}`)).data,
    enabled: options?.enabled !== false,
  })

export const useInternalReferralsGrouped = (by: 'user' | 'company', options?: { enabled?: boolean }) =>
  useQuery<{ by: 'user' | 'company'; data: StaffReferralGroup[] }>({
    queryKey: [...INTERNAL_REFERRALS_QUERY_KEY, 'grouped', by],
    queryFn: async () => (await api.get(`${BASE}/grouped${qs({ by })}`)).data,
    enabled: options?.enabled !== false,
  })

export const useInternalReferralSummary = (range: { from?: string; to?: string } = {}) =>
  useQuery<ReferralProgrammeSummary>({
    queryKey: [...INTERNAL_REFERRALS_QUERY_KEY, 'summary', range],
    queryFn: async () => (await api.get(`${BASE}/summary${qs(range)}`)).data,
  })

/** `from` inclusive, `to` exclusive — ISO dates. The API caches FirstPromoter for 15 minutes. */
export const useAffiliatesDashboard = (range: { from: string; to: string }) =>
  useQuery<AffiliatesDashboard>({
    queryKey: [...INTERNAL_REFERRALS_QUERY_KEY, 'dashboard', range.from, range.to],
    queryFn: async () => (await api.get(`${BASE}/dashboard${qs(range)}`)).data,
    staleTime: 60 * 1000,
    retry: false,
  })

/** `enabled` (VRG-684): the Refer & earn tab does not need FirstPromoter, so it does not call it. */
export const useInsidersFirstPromoter = (range: { from: string; to: string }, opts: { enabled?: boolean } = {}) =>
  useQuery<InsidersFirstPromoterReport>({
    queryKey: [...INTERNAL_REFERRALS_QUERY_KEY, 'firstpromoter', range.from, range.to],
    queryFn: async () => (await api.get(`${BASE}/insiders/firstpromoter${qs(range)}`)).data,
    enabled: opts.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

export const useCompanyReferrals = (companyId: string | undefined) =>
  useQuery<CompanyReferrals>({
    queryKey: [...INTERNAL_REFERRALS_QUERY_KEY, 'company', companyId],
    queryFn: async () => (await api.get(`${BASE}/companies/${companyId}`)).data,
    enabled: !!companyId,
  })

export const useReferralStaffActions = (referralId: string | null) =>
  useQuery<{ data: ReferralStaffAction[] }>({
    queryKey: [...INTERNAL_REFERRALS_QUERY_KEY, 'actions', referralId],
    queryFn: async () => (await api.get(`${BASE}/${referralId}/actions`)).data,
    enabled: !!referralId,
  })

export const useReferralInviteRegions = () =>
  useQuery<ReferralInviteRegion[]>({
    queryKey: [...INTERNAL_REFERRALS_QUERY_KEY, 'regions'],
    queryFn: async () => (await api.get(`${BASE}/invite-regions`)).data,
  })

/** Every staff referral read, plus the waitlist (its rows carry `referral`). */
const useInvalidateReferrals = () => {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: INTERNAL_REFERRALS_QUERY_KEY })
    queryClient.invalidateQueries({ queryKey: [SIGNUPS_QUERY_KEY] })
  }
}

export const useUpdateReferralInviteRegion = () => {
  const invalidate = useInvalidateReferrals()
  return useMutation<
    ReferralInviteRegion,
    unknown,
    { region: ReferralInviteRegion['region']; sending_enabled?: boolean; lawful_basis?: string }
  >({
    mutationFn: async ({ region, ...body }) => (await api.patch(`${BASE}/invite-regions/${region}`, body)).data,
    onSuccess: invalidate,
  })
}

export interface ManualReversalResult {
  created: boolean
  applied: number
  forgiven: number
  credits_net: number
  action: ReferralStaffAction
}

export const useReverseReferralCredits = () => {
  const invalidate = useInvalidateReferrals()
  return useMutation<
    ManualReversalResult,
    unknown,
    { referralId: string; amount: number; reason: string; idempotencyKey: string }
  >({
    mutationFn: async ({ referralId, amount, reason, idempotencyKey }) =>
      (
        await api.post(`${BASE}/${referralId}/reversals`, {
          amount,
          reason,
          idempotency_key: idempotencyKey,
        })
      ).data,
    onSuccess: invalidate,
  })
}

export const useReattributeReferral = () => {
  const invalidate = useInvalidateReferrals()
  return useMutation<
    { created: boolean; action: ReferralStaffAction; referral_id: string },
    unknown,
    {
      referralId: string
      referrerCompanyId: string
      referrerUserId: string | null
      reason: string
      idempotencyKey: string
    }
  >({
    mutationFn: async ({ referralId, referrerCompanyId, referrerUserId, reason, idempotencyKey }) =>
      (
        await api.post(`${BASE}/${referralId}/reattribute`, {
          referrer_company_id: referrerCompanyId,
          referrer_user_id: referrerUserId,
          reason,
          idempotency_key: idempotencyKey,
        })
      ).data,
    onSuccess: invalidate,
  })
}
